/**
 * Analytics event type definitions for the field-sales widget.
 * These types ensure type-safety when tracking user interactions.
 */

import type { ProximityPrecision } from "@acto/gql/graphql";
import type { StandardPeriod } from "@acto/taskpane/core/domain";

// Chat Events
export interface ChatOpenedProperties {
  source: "floating_input" | "chat_page" | "customer_details";
  customerId?: string;
  customerName?: string;
}

export interface ChatMessageSentProperties {
  hasCustomerContext: boolean;
  customerId?: string;
  messageLength: number;
  message: string;
}

// Customer Events
export interface CustomerSearchProperties {
  searchTerm: string;
  resultCount: number;
  resultCountByMatchedField?: Record<string, number>;
}

export type QuickActionSource = "home" | "customers_page" | "calendar" | "customer_details";

/** The nearby-customers entry a booking started from, orthogonal to the page-level `source`. */
export type PlanOrigin = "nearby_suggestion" | "nearby_search";

export interface CustomerPlannedProperties {
  customerId: string;
  customerName?: string;
  scheduledDate: string;
  hasExistingMeeting: boolean;
  isRecurring: boolean;
  cadence?: string;
  source: QuickActionSource;
  planOrigin?: PlanOrigin;
}

// Meeting Prep Events
export interface MeetingPrepOpenedProperties {
  customerId: string;
  customerExternalReference: string;
  customerName: string;
}

export type MeetingPrepFeedbackReason = "data_incorrect" | "content_irrelevant";

export interface MeetingPrepCardHiddenProperties {
  customerId: string;
  // The talking point's `type`.
  cardType: string;
  reason: MeetingPrepFeedbackReason;
  reasonText: string;
  countsTowardThreshold: boolean;
}

export interface MeetingPrepCardUnhiddenProperties {
  customerId: string;
  cardType: string;
  unhideType: "always_show" | "just_once";
}

export interface MeetingPrepStoryFeedbackGivenProperties {
  customerId: string;
  // The story's category.
  storyType: string;
  reason: MeetingPrepFeedbackReason;
  reasonText: string;
  // The number of cards in the story, all covered by the feedback.
  cardCount: number;
}

export interface ProductExclusionCreatedProperties {
  customerId: string;
  // all_products = every product the card still offered was excluded.
  exclusionType: "specific_products" | "all_products";
  productIds: string[];
  // once = excluded for the rest of today.
  duration: "once" | "indefinite" | "snoozed";
  // ISO date, set only for a snoozed exclusion.
  snoozeUntil?: string;
}

// Note Events
export interface NoteCreatedProperties {
  noteId: string;
  customerId: string;
  customerName: string;
  contentLength: number;
  note: string;
  noteType: "note" | "meeting_recap";
  wasVoiceRecorded?: boolean;
  mode?: "batch" | "single";
}

/** Navigation origin of a customer-details open; "other" covers unrecognized origins. */
export type NavigationSource = "home" | "customers_page" | "calendar" | "todos_overview" | "other";

// Customer Details Events
export interface CustomerDetailsOpenedProperties {
  customerId: string;
  customerName: string;
  // Set only where the opening surface is known.
  source?: NavigationSource;
  // Both set only when the customer was opened from a search result. Position is 1-based.
  searchMatchFields?: string[];
  searchResultPosition?: number;
}

// Recommended Customer Events
export interface RecommendedCustomerClickedProperties {
  customerId: string;
  customerName: string;
  customerExternalReference?: string;
  source: "home" | "customers_page";
}

export interface RecommendedCustomerDismissedProperties {
  customerId: string;
  customerName?: string;
  customerExternalReference?: string;
  dismissedUntil: string;
  // Optional — not every dismiss surface collects a reason.
  reason?: string;
  source: QuickActionSource;
}

// Todo Events
export interface TodoCompletedProperties {
  source: "workflow" | "recap" | "manual" | "meeting_prep_card";
  todoId: string;
  customerId?: string;
  customerName?: string;
}

export type TodoSurfaceSource = "customer_details" | "todos_overview";

export interface TodosViewedProperties {
  // overdue_banner = the Home "überfällige Aufgaben" pill; todos_overview = the bottom-nav tab.
  source: TodoSurfaceSource | "overdue_banner";
  customerId?: string;
  customerName?: string;
}

export interface TodoCreatedProperties {
  todoId?: string;
  customerId?: string;
  customerName?: string;
  source: TodoSurfaceSource;
  hasDueDate: boolean;
}

// Revenue/Product Metrics Events (FS1-only surfaces)
export interface RevenueMetricsViewedProperties {
  customerId: string;
  period: StandardPeriod;
  activeTab: "revenue" | "margin";
}

export type ProductMetricsViewedProperties = RevenueMetricsViewedProperties;

// Management View Events
export interface MVSearchEmployeeProperties {
  searchTerm: string;
  resultCount?: number;
}

export interface MVOpenEmployeeDetailsProperties {
  employeeId: string;
  employeeName: string;
}

export type MVOpenScheduledAppointmentsProperties = MVOpenEmployeeDetailsProperties;
export type MVOpenCompletedAppointmentsProperties = MVOpenEmployeeDetailsProperties;
export type MVOpenNoReportAppointmentsProperties = MVOpenEmployeeDetailsProperties;
export type MVOpenDidNotTakePlaceAppointmentsProperties = MVOpenEmployeeDetailsProperties;
export type MVOpenIgnoredCustomersProperties = MVOpenEmployeeDetailsProperties;
export type MVOpenMarkedDoneCustomersProperties = MVOpenEmployeeDetailsProperties;
export type MVOpenEmployeeTasksProperties = MVOpenEmployeeDetailsProperties;

export type MVSearchCustomerProperties = MVSearchEmployeeProperties;

export interface MVOpenCustomerProperties {
  customerId: string;
  customerName: string;
}

// Management View - Workflow Events
export interface MVWorkflowBaseProperties {
  workflowId: string;
  workflowName: string;
}

export interface MVWorkflowCreatedProperties extends MVWorkflowBaseProperties {
  isRepeating: boolean;
  hasFixedResponsibleEmployee: boolean;
  conditionCount: number;
}

export type MVWorkflowUpdatedProperties = MVWorkflowCreatedProperties;

export interface MVWorkflowDetailsOpenedProperties extends MVWorkflowBaseProperties {
  isActive: boolean;
  activeTodosCount: number;
}

export type MVWorkflowDeletedProperties = MVWorkflowBaseProperties;
export type MVWorkflowActivatedProperties = MVWorkflowBaseProperties;
export type MVWorkflowDeactivatedProperties = MVWorkflowBaseProperties;

export type AnalysisMetric = "revenue" | "margin" | "orders" | "profit";

export interface AnalysisFilterProperties {
  period?: string;
  periodType?: "predefined" | "custom";
  productGroup?: string;
  quickFilter?: string;
}

export interface CustomerAnalysisViewedProperties {
  customerId: string;
  metric: AnalysisMetric;
  filters?: AnalysisFilterProperties;
}

export interface DashboardAnalysisViewedProperties {
  sortBy: "product" | "customer";
  metric: AnalysisMetric;
  filters?: AnalysisFilterProperties;
}

export interface RecapOpenedProperties {
  recapId: string;
  customerId: string;
}

export type RecapEditedProperties = RecapOpenedProperties;

// Fired when the backend AI summarize step for a freshly created recap completes — the step that
// also extracts the recap's to-dos, which don't exist yet when NOTE_CREATED fires at save-time.
export interface RecapSummaryGeneratedProperties {
  noteId: string;
  customerId: string;
  customerName?: string;
  todosCreated: boolean;
  todosCreatedCount: number;
}

export interface OrderOpenedProperties {
  orderId: string;
  customerId: string;
  customerName?: string;
}

export interface OrdersViewedProperties {
  customerId: string;
  customerName?: string;
}

export interface OfferOpenedProperties {
  offerId: string;
  customerId: string;
  customerName?: string;
}

export type OffersViewedProperties = OrdersViewedProperties;

export type RecapsViewedProperties = OrdersViewedProperties;

export type CustomerListViewedProperties = Record<string, never>;

export type CalendarViewedProperties = Record<string, never>;

export interface AppointmentOpenedProperties {
  appointmentId: string;
  customerId: string;
  customerName?: string;
  scheduledDate?: string;
}

export interface CustomerMarkedDoneProperties {
  customerId: string;
  reason: "mail" | "call" | "other";
  withRecap: boolean;
  source: QuickActionSource;
}

export interface NearbyCustomersOpenedProperties {
  dayIso?: string;
  meetingCount: number;
}

export interface NearbyReferenceChangedProperties {
  meetingId: string;
  customerId: string;
}

export interface NearbySuggestionPlannedProperties {
  customerId: string;
  precision: ProximityPrecision | null;
}

export interface NearbySearchOpenedProperties {
  // 0 = the ranked list loaded empty; null = it had not loaded (pending or failed).
  suggestionCount: number | null;
}

export interface NextMeetingMapsOpenedProperties {
  appointmentId: string;
  customerId: string;
  customerName?: string;
}

/**
 * Ties each event name to its payload shape. `track` is generic over this map, so firing an
 * event with another event's payload is a compile error.
 */
export interface AnalyticsEventPropertiesMap {
  [AnalyticsEvents.CHAT_OPENED]: ChatOpenedProperties;
  [AnalyticsEvents.CHAT_MESSAGE_SENT]: ChatMessageSentProperties;
  [AnalyticsEvents.CUSTOMER_SEARCH]: CustomerSearchProperties;
  [AnalyticsEvents.CUSTOMER_PLANNED]: CustomerPlannedProperties;
  [AnalyticsEvents.CUSTOMER_DETAILS_OPENED]: CustomerDetailsOpenedProperties;
  [AnalyticsEvents.MEETING_PREP_OPENED]: MeetingPrepOpenedProperties;
  [AnalyticsEvents.MEETING_PREP_CARD_HIDDEN]: MeetingPrepCardHiddenProperties;
  [AnalyticsEvents.MEETING_PREP_CARD_UNHIDDEN]: MeetingPrepCardUnhiddenProperties;
  [AnalyticsEvents.MEETING_PREP_STORY_FEEDBACK_GIVEN]: MeetingPrepStoryFeedbackGivenProperties;
  [AnalyticsEvents.PRODUCT_EXCLUSION_CREATED]: ProductExclusionCreatedProperties;
  [AnalyticsEvents.NOTE_CREATED]: NoteCreatedProperties;
  [AnalyticsEvents.RECOMMENDED_CUSTOMER_CLICKED]: RecommendedCustomerClickedProperties;
  [AnalyticsEvents.RECOMMENDED_CUSTOMER_DISMISSED]: RecommendedCustomerDismissedProperties;
  [AnalyticsEvents.TODO_COMPLETED]: TodoCompletedProperties;
  [AnalyticsEvents.TODO_REOPENED]: TodoCompletedProperties;
  [AnalyticsEvents.REVENUE_METRICS_VIEWED]: RevenueMetricsViewedProperties;
  [AnalyticsEvents.PRODUCT_METRICS_VIEWED]: ProductMetricsViewedProperties;
  [AnalyticsEvents.MV_SEARCH_EMPLOYEE]: MVSearchEmployeeProperties;
  [AnalyticsEvents.MV_OPEN_EMPLOYEE_DETAILS]: MVOpenEmployeeDetailsProperties;
  [AnalyticsEvents.MV_OPEN_SCHEDULED_APPOINTMENTS]: MVOpenScheduledAppointmentsProperties;
  [AnalyticsEvents.MV_OPEN_COMPLETED_APPOINTMENTS]: MVOpenCompletedAppointmentsProperties;
  [AnalyticsEvents.MV_OPEN_NO_REPORT_APPOINTMENTS]: MVOpenNoReportAppointmentsProperties;
  [AnalyticsEvents.MV_OPEN_DID_NOT_TAKE_PLACE_APPOINTMENTS]: MVOpenDidNotTakePlaceAppointmentsProperties;
  [AnalyticsEvents.MV_OPEN_IGNORED_CUSTOMERS]: MVOpenIgnoredCustomersProperties;
  [AnalyticsEvents.MV_OPEN_MARKED_DONE_CUSTOMERS]: MVOpenMarkedDoneCustomersProperties;
  [AnalyticsEvents.MV_OPEN_EMPLOYEE_TASKS]: MVOpenEmployeeTasksProperties;
  [AnalyticsEvents.MV_SEARCH_CUSTOMER]: MVSearchCustomerProperties;
  [AnalyticsEvents.MV_OPEN_CUSTOMER]: MVOpenCustomerProperties;
  [AnalyticsEvents.MV_WORKFLOW_CREATED]: MVWorkflowCreatedProperties;
  [AnalyticsEvents.MV_WORKFLOW_UPDATED]: MVWorkflowUpdatedProperties;
  [AnalyticsEvents.MV_WORKFLOW_DELETED]: MVWorkflowDeletedProperties;
  [AnalyticsEvents.MV_WORKFLOW_ACTIVATED]: MVWorkflowActivatedProperties;
  [AnalyticsEvents.MV_WORKFLOW_DEACTIVATED]: MVWorkflowDeactivatedProperties;
  [AnalyticsEvents.MV_WORKFLOW_DETAILS_OPENED]: MVWorkflowDetailsOpenedProperties;
  [AnalyticsEvents.CUSTOMER_ANALYSIS_VIEWED]: CustomerAnalysisViewedProperties;
  [AnalyticsEvents.DASHBOARD_ANALYSIS_VIEWED]: DashboardAnalysisViewedProperties;
  [AnalyticsEvents.RECAP_OPENED]: RecapOpenedProperties;
  [AnalyticsEvents.RECAP_SUMMARY_GENERATED]: RecapSummaryGeneratedProperties;
  [AnalyticsEvents.RECAP_EDITED]: RecapEditedProperties;
  [AnalyticsEvents.ORDER_OPENED]: OrderOpenedProperties;
  [AnalyticsEvents.OFFER_OPENED]: OfferOpenedProperties;
  [AnalyticsEvents.CALENDAR_VIEWED]: CalendarViewedProperties;
  [AnalyticsEvents.APPOINTMENT_OPENED]: AppointmentOpenedProperties;
  [AnalyticsEvents.NEARBY_CUSTOMERS_OPENED]: NearbyCustomersOpenedProperties;
  [AnalyticsEvents.NEARBY_REFERENCE_CHANGED]: NearbyReferenceChangedProperties;
  [AnalyticsEvents.NEARBY_SUGGESTION_PLANNED]: NearbySuggestionPlannedProperties;
  [AnalyticsEvents.NEARBY_SEARCH_OPENED]: NearbySearchOpenedProperties;
  [AnalyticsEvents.NEXT_MEETING_MAPS_OPENED]: NextMeetingMapsOpenedProperties;
  [AnalyticsEvents.CUSTOMER_MARKED_DONE]: CustomerMarkedDoneProperties;
  [AnalyticsEvents.TODOS_VIEWED]: TodosViewedProperties;
  [AnalyticsEvents.ORDERS_VIEWED]: OrdersViewedProperties;
  [AnalyticsEvents.OFFERS_VIEWED]: OffersViewedProperties;
  [AnalyticsEvents.RECAPS_VIEWED]: RecapsViewedProperties;
  [AnalyticsEvents.CUSTOMER_LIST_VIEWED]: CustomerListViewedProperties;
  [AnalyticsEvents.TODO_CREATED]: TodoCreatedProperties;
}

export type AnalyticsEventProperties = AnalyticsEventPropertiesMap[AnalyticsEventName];

// Event names as constants for type safety
export const AnalyticsEvents = {
  CHAT_OPENED: "Widget Chat Opened",
  CHAT_MESSAGE_SENT: "Widget Chat Message Sent",
  CUSTOMER_SEARCH: "Widget Customer Search",
  CUSTOMER_PLANNED: "Widget Customer Planned",
  CUSTOMER_DETAILS_OPENED: "Widget Customer Details Opened",
  MEETING_PREP_OPENED: "Widget Meeting Prep Opened",
  MEETING_PREP_CARD_HIDDEN: "Widget Meeting Prep Card Hidden",
  MEETING_PREP_CARD_UNHIDDEN: "Widget Meeting Prep Card Unhidden",
  MEETING_PREP_STORY_FEEDBACK_GIVEN: "Widget Meeting Prep Story Feedback Given",
  PRODUCT_EXCLUSION_CREATED: "Widget Product Exclusion Created",
  NOTE_CREATED: "Widget Note Created",
  RECOMMENDED_CUSTOMER_CLICKED: "Widget Recommended Customer Clicked",
  RECOMMENDED_CUSTOMER_DISMISSED: "Widget Recommended Customer Dismissed",
  TODO_COMPLETED: "Widget Todo Completed",
  TODO_REOPENED: "Widget Todo Reopened",
  REVENUE_METRICS_VIEWED: "Widget Revenue Metrics Viewed",
  PRODUCT_METRICS_VIEWED: "Widget Product Metrics Viewed",
  MV_SEARCH_EMPLOYEE: "Widget MV Search Employee",
  MV_OPEN_EMPLOYEE_DETAILS: "Widget MV Open Employee Details",
  MV_OPEN_SCHEDULED_APPOINTMENTS: "Widget MV Open Scheduled Appointments",
  MV_OPEN_COMPLETED_APPOINTMENTS: "Widget MV Open Completed Appointments",
  MV_OPEN_NO_REPORT_APPOINTMENTS: "Widget MV Open No Report Appointments",
  MV_OPEN_DID_NOT_TAKE_PLACE_APPOINTMENTS: "Widget MV Open Did Not Take Place Appointments",
  MV_OPEN_IGNORED_CUSTOMERS: "Widget MV Open Ignored Customers",
  MV_OPEN_MARKED_DONE_CUSTOMERS: "Widget MV Open Marked Done Customers",
  MV_OPEN_EMPLOYEE_TASKS: "Widget MV Open Employee Tasks",
  MV_SEARCH_CUSTOMER: "Widget MV Search Customer",
  MV_OPEN_CUSTOMER: "Widget MV Open Customer",
  MV_WORKFLOW_CREATED: "Widget MV Workflow Created",
  MV_WORKFLOW_UPDATED: "Widget MV Workflow Updated",
  MV_WORKFLOW_DELETED: "Widget MV Workflow Deleted",
  MV_WORKFLOW_ACTIVATED: "Widget MV Workflow Activated",
  MV_WORKFLOW_DEACTIVATED: "Widget MV Workflow Deactivated",
  MV_WORKFLOW_DETAILS_OPENED: "Widget MV Workflow Details Opened",
  CUSTOMER_ANALYSIS_VIEWED: "Widget Customer Analysis Viewed",
  DASHBOARD_ANALYSIS_VIEWED: "Widget Dashboard Analysis Viewed",
  RECAP_OPENED: "Widget Recap Opened",
  RECAP_SUMMARY_GENERATED: "Widget Recap Summary Generated",
  RECAP_EDITED: "Widget Recap Edited",
  ORDER_OPENED: "Widget Order Opened",
  OFFER_OPENED: "Widget Offer Opened",
  CALENDAR_VIEWED: "Widget Calendar Viewed",
  APPOINTMENT_OPENED: "Widget Appointment Opened",
  NEARBY_CUSTOMERS_OPENED: "Widget Nearby Customers Opened",
  NEARBY_REFERENCE_CHANGED: "Widget Nearby Reference Changed",
  NEARBY_SUGGESTION_PLANNED: "Widget Nearby Suggestion Planned",
  NEARBY_SEARCH_OPENED: "Widget Nearby Search Opened",
  NEXT_MEETING_MAPS_OPENED: "Widget Next Meeting Maps Opened",
  CUSTOMER_MARKED_DONE: "Widget Customer Marked Done",
  TODOS_VIEWED: "Widget Todos Viewed",
  ORDERS_VIEWED: "Widget Orders Viewed",
  OFFERS_VIEWED: "Widget Offers Viewed",
  RECAPS_VIEWED: "Widget Recaps Viewed",
  CUSTOMER_LIST_VIEWED: "Widget Customer List Viewed",
  TODO_CREATED: "Widget Todo Created",
} as const;

export type AnalyticsEventName = (typeof AnalyticsEvents)[keyof typeof AnalyticsEvents];

// Context passed with every event
export interface AnalyticsContext {
  track: <E extends AnalyticsEventName>(eventName: E, properties: AnalyticsEventPropertiesMap[E]) => void;
  identify: (userId: string, traits?: Record<string, unknown>) => void;
  isReady: boolean;
}
